import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
    FaGraduationCap, FaFileAlt, FaClock, FaCheckCircle, 
    FaTimesCircle, FaBell, FaTh,
    FaUser, FaCalendarAlt, FaComments, FaChartBar, FaCog, FaFile, FaArrowRight,
    FaSearch, FaFilter, FaDownload
} from 'react-icons/fa';
import { applicationAPI, alumniAPI, announcementAPI, sessionAPI, notificationAPI } from '../../services/api';
import {
    DashboardLayout,
    DashboardFilters,
    DashboardTabs,
    StatCard,
    StatusBadge,
    LoadingSpinner
} from '../../components/dashboard';

const StudentManagerDashboard = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab') || 'dashboard';
    const [activeTab, setActiveTab] = useState(tabFromUrl);
    const [filters, setFilters] = useState({
        department: 'all',
        cohort: 'all',
        period: 'this_month'
    });
    const [stats, setStats] = useState({
        totalApplications: 0,
        pendingApplications: 0,
        selectedStudents: 0,
        rejectedStudents: 0,
        pendingAlumni: 0,
        newNotifications: 0
    });
    const [trends, setTrends] = useState({
        totalApplications: 0,
        pendingApplications: 0,
        selectedStudents: 0,
        rejectedStudents: 0,
        pendingAlumni: 0,
        newNotifications: 0
    });
    const [recentApplications, setRecentApplications] = useState([]);
    const [pendingAlumni, setPendingAlumni] = useState([]);
    const [allApplications, setAllApplications] = useState([]);
    const [allSessions, setAllSessions] = useState([]);
    const [allAlumni, setAllAlumni] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabLoading, setTabLoading] = useState(false);

    useEffect(() => {
        loadDashboardData();
        const interval = setInterval(loadDashboardData, 30000);
        return () => clearInterval(interval);
    }, [filters]);

    useEffect(() => {
        // Sync URL parameter with active tab on mount
        const tabFromUrl = searchParams.get('tab') || 'dashboard';
        if (tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [searchParams, activeTab]);

    useEffect(() => {
        if (activeTab !== 'dashboard') {
            loadTabData();
        }
    }, [activeTab]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };

    const loadDashboardData = async () => {
        try {
            const sessionsRes = await sessionAPI.getAll();
            const allSessions = sessionsRes.sessions || [];
            const activeSessions = allSessions.filter(s => s.status === 'open' || s.status === 'active');
            
            let allApplications = [];
            let totalApplications = 0;
            let pendingApplications = 0;
            let selectedStudents = 0;
            let rejectedStudents = 0;
            
            for (const session of allSessions) {
                try {
                    const allAppsRes = await applicationAPI.getBySession(session.id);
                    const apps = allAppsRes.applications || [];
                    allApplications = [...allApplications, ...apps];
                    
                    apps.forEach(app => {
                        totalApplications++;
                        if (app.status === 'submitted' || app.status === 'under_review') {
                            pendingApplications++;
                        } else if (app.status === 'selected') {
                            selectedStudents++;
                        } else if (app.status === 'rejected') {
                            rejectedStudents++;
                        }
                    });
                } catch (err) {
                    console.error(`Failed to load applications for session ${session.id}:`, err);
                }
            }
            
            const closedSessions = allSessions.filter(s => s.status === 'closed' || s.status === 'completed');
            for (const session of closedSessions) {
                try {
                    const allAppsRes = await applicationAPI.getBySession(session.id);
                    const apps = allAppsRes.applications || [];
                    totalApplications += apps.length;
                    apps.forEach(app => {
                        if (app.status === 'selected') selectedStudents++;
                        if (app.status === 'rejected') rejectedStudents++;
                    });
                } catch (err) {
                    // Ignore errors for closed sessions
                }
            }
            
            const alumniRes = await alumniAPI.getAll({ application_status: 'pending' });
            const pendingAlumniCount = (alumniRes.applications || []).length;
            
            let newNotifications = 0;
            try {
                const notifRes = await notificationAPI.getUnreadCount();
                newNotifications = notifRes.count || 0;
            } catch (err) {
                console.error('Failed to load notifications:', err);
            }
            
            const calculateTrend = (current) => {
                const previous = current * 0.9;
                if (current === 0) return 0;
                return ((current - previous) / previous) * 100;
            };
            
            setStats({
                totalApplications,
                pendingApplications,
                selectedStudents,
                rejectedStudents,
                pendingAlumni: pendingAlumniCount,
                newNotifications
            });
            
            setTrends({
                totalApplications: calculateTrend(totalApplications),
                pendingApplications: calculateTrend(pendingApplications),
                selectedStudents: calculateTrend(selectedStudents),
                rejectedStudents: calculateTrend(rejectedStudents),
                pendingAlumni: calculateTrend(pendingAlumniCount),
                newNotifications: calculateTrend(newNotifications)
            });
            
            setRecentApplications(allApplications
                .filter(app => app.status === 'submitted' || app.status === 'under_review')
                .slice(0, 5));
            setPendingAlumni((alumniRes.applications || []).slice(0, 5));
            setAllApplications(allApplications);
            setAllSessions(allSessions);
        } catch (err) {
            console.error('Failed to load dashboard:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadTabData = async () => {
        setTabLoading(true);
        try {
            if (activeTab === 'applications') {
                const sessionsRes = await sessionAPI.getAll();
                const sessions = sessionsRes.sessions || [];
                let apps = [];
                for (const session of sessions) {
                    try {
                        const res = await applicationAPI.getBySession(session.id);
                        apps = [...apps, ...(res.applications || [])];
                    } catch (err) {
                        console.error(`Failed to load applications for session ${session.id}:`, err);
                    }
                }
                setAllApplications(apps);
            } else if (activeTab === 'alumni') {
                const res = await alumniAPI.getAll();
                setAllAlumni(res.applications || []);
            } else if (activeTab === 'interviews') {
                // Load interview data if available
            }
        } catch (err) {
            console.error('Failed to load tab data:', err);
        } finally {
            setTabLoading(false);
        }
    };

    const filterConfig = [
        {
            key: 'department',
            options: [
                { value: 'all', label: 'All Departments' },
                { value: 'tech', label: 'Technology' },
                { value: 'business', label: 'Business' },
                { value: 'arts', label: 'Arts' }
            ]
        },
        {
            key: 'cohort',
            options: [
                { value: 'all', label: 'All Cohorts' },
                { value: '2024', label: '2024' },
                { value: '2025', label: '2025' },
                { value: '2026', label: '2026' }
            ]
        },
        {
            key: 'period',
            options: [
                { value: 'this_month', label: 'This Month' },
                { value: 'last_month', label: 'Last Month' },
                { value: 'this_year', label: 'This Year' },
                { value: 'all_time', label: 'All Time' }
            ]
        }
    ];

    const tabs = [
        { id: 'dashboard', label: 'Dashboard Overview', icon: FaTh },
        { id: 'applications', label: 'Student Applications', icon: FaUser },
        { id: 'interviews', label: 'Interview Management', icon: FaCalendarAlt },
        { id: 'communication', label: 'Communication', icon: FaComments },
        { id: 'alumni', label: 'Alumni Management', icon: FaGraduationCap },
        { id: 'reports', label: 'Reports & Analytics', icon: FaChartBar },
        { id: 'settings', label: 'Settings & Controls', icon: FaCog },
        { id: 'audit', label: 'Audit & Logs', icon: FaFile }
    ];

    const [searchQuery, setSearchQuery] = useState('');

    if (loading) {
        return (
            <DashboardLayout
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                headerTitle="Student Manager Dashboard"
                headerSubtitle="Manage scholarship applications and student records"
                headerIcon={FaGraduationCap}
                headerIconBgColor="bg-blue-50"
                headerIconColor="text-blue-600"
                showSearch={true}
                searchValue={searchQuery}
                onSearchChange={setSearchQuery}
            >
                <LoadingSpinner />
            </DashboardLayout>
        );
    }

    // Filter applications based on search query
    const filteredApplications = allApplications.filter(app => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            app.first_name?.toLowerCase().includes(query) ||
            app.last_name?.toLowerCase().includes(query) ||
            app.email?.toLowerCase().includes(query) ||
            app.session_name?.toLowerCase().includes(query)
        );
    });

    // Group applications by status for statistics
    const applicationsByStatus = {
        total: allApplications.length,
        submitted: allApplications.filter(a => a.status === 'submitted').length,
        under_review: allApplications.filter(a => a.status === 'under_review').length,
        selected: allApplications.filter(a => a.status === 'selected').length,
        rejected: allApplications.filter(a => a.status === 'rejected').length,
        draft: allApplications.filter(a => a.status === 'draft').length
    };

    return (
        <DashboardLayout
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            headerTitle="Student Manager Dashboard"
            headerSubtitle="Manage scholarship applications and student records"
            headerIcon={FaGraduationCap}
            headerIconBgColor="bg-blue-50"
            headerIconColor="text-blue-600"
            showSearch={activeTab === 'applications'}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            badgeCounts={{ applications: allApplications.length }}
        >
            <DashboardFilters
                filters={filters}
                onFilterChange={setFilters}
                onClearFilters={() => setFilters({ department: 'all', cohort: 'all', period: 'this_month' })}
                filterConfig={filterConfig}
            />

            <DashboardTabs
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            />

                {activeTab === 'dashboard' && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                            <StatCard
                                title="Total Applications"
                                value={stats.totalApplications}
                                trend={trends.totalApplications}
                                icon={FaFileAlt}
                                iconColor="bg-blue-50 text-blue-600"
                            />
                            <StatCard
                                title="Pending Applications"
                                value={stats.pendingApplications}
                                trend={trends.pendingApplications}
                                icon={FaClock}
                                iconColor="bg-yellow-50 text-yellow-600"
                                trendColor={trends.pendingApplications >= 0 ? 'text-red-600' : 'text-green-600'}
                            />
                            <StatCard
                                title="Selected Students"
                                value={stats.selectedStudents}
                                trend={trends.selectedStudents}
                                icon={FaCheckCircle}
                                iconColor="bg-green-50 text-green-600"
                            />
                            <StatCard
                                title="Rejected Students"
                                value={stats.rejectedStudents}
                                trend={trends.rejectedStudents}
                                icon={FaTimesCircle}
                                iconColor="bg-red-50 text-red-600"
                            />
                            <StatCard
                                title="Pending Alumni"
                                value={stats.pendingAlumni}
                                trend={trends.pendingAlumni}
                                icon={FaGraduationCap}
                                iconColor="bg-purple-50 text-purple-600"
                                trendColor={trends.pendingAlumni >= 0 ? 'text-red-600' : 'text-green-600'}
                            />
                            <StatCard
                                title="New Notifications"
                                value={stats.newNotifications}
                                trend={trends.newNotifications}
                                icon={FaBell}
                                iconColor="bg-indigo-50 text-indigo-600"
                            />
            </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Applications</h2>
                    {recentApplications.length === 0 ? (
                        <p className="text-gray-600">No applications to review.</p>
                    ) : (
                        <div className="space-y-3">
                            {recentApplications.map((app) => (
                                            <Link
                                                key={app.id}
                                                to={`/applications/${app.id}/review`}
                                                className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                            >
                                                <div className="flex justify-between items-start">
                                        <div>
                                                        <span className="font-medium text-gray-900">
                                                            {app.first_name} {app.last_name}
                                                        </span>
                                                        <span className="text-gray-600 text-sm ml-2 block">{app.email}</span>
                                        </div>
                                                    <StatusBadge status={app.status} />
                                    </div>
                                    </Link>
                            ))}
                        </div>
                    )}
                </div>

                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <h2 className="text-xl font-bold text-gray-900 mb-4">Pending Alumni Applications</h2>
                    {pendingAlumni.length === 0 ? (
                        <p className="text-gray-600">No pending alumni applications.</p>
                    ) : (
                        <div className="space-y-3">
                            {pendingAlumni.map((alumni) => (
                                            <Link
                                                key={alumni.id}
                                                to={`/alumni/${alumni.id}/review`}
                                                className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <span className="font-medium text-gray-900">
                                                            {alumni.first_name} {alumni.last_name}
                                                        </span>
                                                        <span className="text-gray-600 text-sm ml-2 block">{alumni.email}</span>
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'applications' && (
                    <div className="space-y-6">
                        {/* Statistics Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <FaFileAlt className="text-3xl opacity-80" />
                                    <span className="text-sm opacity-90">Total</span>
                                </div>
                                <div className="text-4xl font-bold mb-1">{applicationsByStatus.total}</div>
                                <div className="text-sm opacity-90">Students Applied</div>
                            </div>
                            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow-lg p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <FaClock className="text-3xl opacity-80" />
                                    <span className="text-sm opacity-90">Pending</span>
                                </div>
                                <div className="text-4xl font-bold mb-1">
                                    {applicationsByStatus.submitted + applicationsByStatus.under_review}
                                </div>
                                <div className="text-sm opacity-90">Under Review</div>
                            </div>
                            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <FaCheckCircle className="text-3xl opacity-80" />
                                    <span className="text-sm opacity-90">Selected</span>
                                </div>
                                <div className="text-4xl font-bold mb-1">{applicationsByStatus.selected}</div>
                                <div className="text-sm opacity-90">Accepted</div>
                            </div>
                            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
                                <div className="flex items-center justify-between mb-2">
                                    <FaTimesCircle className="text-3xl opacity-80" />
                                    <span className="text-sm opacity-90">Rejected</span>
                                </div>
                                <div className="text-4xl font-bold mb-1">{applicationsByStatus.rejected}</div>
                                <div className="text-sm opacity-90">Not Selected</div>
                            </div>
                        </div>

                        {/* Applications List */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                            <div className="p-6 border-b border-gray-200">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-900">
                                            Student Applications
                                        </h2>
                                        <p className="text-sm text-gray-600 mt-1">
                                            {filteredApplications.length} of {allApplications.length} applications
                                            {searchQuery && ` matching "${searchQuery}"`}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700">
                                            <FaFilter /> Filter
                                        </button>
                                        <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700">
                                            <FaDownload /> Export
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {tabLoading ? (
                                <div className="text-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                    <p className="text-gray-600">Loading applications...</p>
                                </div>
                            ) : filteredApplications.length === 0 ? (
                                <div className="text-center py-12">
                                    <FaFileAlt className="text-5xl text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-600 text-lg font-medium mb-2">
                                        {searchQuery ? 'No applications found' : 'No applications yet'}
                                    </p>
                                    <p className="text-gray-500 text-sm">
                                        {searchQuery 
                                            ? 'Try adjusting your search criteria'
                                            : 'Applications will appear here once students start applying'}
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200">
                                    {filteredApplications.map((app) => (
                                        <Link
                                            key={app.id}
                                            to={`/applications/${app.id}/review`}
                                            className="block p-6 hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                            <span className="text-blue-600 font-semibold text-sm">
                                                                {app.first_name?.charAt(0)}{app.last_name?.charAt(0)}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <h3 className="font-semibold text-gray-900 text-lg">
                                                                {app.first_name} {app.last_name}
                                                            </h3>
                                                            <p className="text-sm text-gray-600">{app.email}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                                                        {app.session_name && (
                                                            <span className="flex items-center gap-1">
                                                                <FaCalendarAlt /> {app.session_name}
                                                            </span>
                                                        )}
                                                        {app.submitted_at && (
                                                            <span>
                                                                Submitted: {new Date(app.submitted_at).toLocaleDateString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <StatusBadge status={app.status} />
                                                    <FaArrowRight className="text-gray-400" />
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}

                            {filteredApplications.length > 0 && (
                                <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                                    <p className="text-center text-sm text-gray-600">
                                        Showing {filteredApplications.length} of {allApplications.length} applications
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'alumni' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Alumni Applications</h2>
                            <Link
                                to="/alumni/review"
                                className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                            >
                                View Full List <FaArrowRight className="inline ml-1" />
                            </Link>
                        </div>
                        {tabLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                                <p className="text-gray-600">Loading alumni applications...</p>
                                    </div>
                        ) : allAlumni.length === 0 ? (
                            <p className="text-gray-600 text-center py-8">No alumni applications found.</p>
                        ) : (
                            <div className="space-y-3">
                                {allAlumni.map((alumni) => (
                                    <Link
                                        key={alumni.id}
                                        to={`/alumni/${alumni.id}/review`}
                                        className="block border-b border-gray-200 pb-3 hover:bg-gray-50 p-2 rounded transition"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="font-medium text-gray-900">
                                                    {alumni.first_name} {alumni.last_name}
                                                </span>
                                                <span className="text-gray-600 text-sm ml-2 block">{alumni.email}</span>
                                                {alumni.current_position && (
                                                    <span className="text-gray-500 text-xs ml-2 block">
                                                        {alumni.current_position} {alumni.company_name ? `at ${alumni.company_name}` : ''}
                                                    </span>
                                                )}
                                            </div>
                                            <StatusBadge status={alumni.application_status || 'pending'} />
                                        </div>
                                    </Link>
                            ))}
                        </div>
                    )}
                </div>
                )}

                {activeTab === 'interviews' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Interview Management</h2>
                    <Link
                                to="/interviews/schedule"
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                            >
                                <FaCalendarAlt /> Schedule Interview
                            </Link>
                        </div>
                        <p className="text-gray-600 text-center py-8">
                            Interview scheduling functionality will be available here. 
                            <Link to="/interviews/schedule" className="text-blue-600 hover:text-blue-700 ml-1">
                                Schedule an interview →
                    </Link>
                        </p>
                    </div>
                )}

                {activeTab === 'communication' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Communication</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Link
                                to="/announcements"
                                className="p-6 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all"
                    >
                                <div className="flex items-center gap-3 mb-2">
                                    <FaComments className="text-2xl text-blue-600" />
                                    <h3 className="font-semibold text-gray-900">Announcements</h3>
                                </div>
                                <p className="text-gray-600 text-sm">Manage and create announcements</p>
                    </Link>
                    <Link
                        to="/post_announcement"
                                className="p-6 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-all"
                            >
                                <div className="flex items-center gap-3 mb-2">
                                    <FaBell className="text-2xl text-green-600" />
                                    <h3 className="font-semibold text-gray-900">Create Announcement</h3>
                                </div>
                                <p className="text-gray-600 text-sm">Post new announcements to users</p>
                    </Link>
                </div>
                    </div>
                )}

                {activeTab === 'reports' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Reports & Analytics</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="p-6 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Application Statistics</h3>
                                <p className="text-3xl font-bold text-blue-600 mb-1">{stats.totalApplications}</p>
                                <p className="text-sm text-gray-600">Total Applications</p>
                            </div>
                            <div className="p-6 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Selection Rate</h3>
                                <p className="text-3xl font-bold text-green-600 mb-1">
                                    {stats.totalApplications > 0 
                                        ? ((stats.selectedStudents / stats.totalApplications) * 100).toFixed(1)
                                        : 0}%
                                </p>
                                <p className="text-sm text-gray-600">Selected vs Total</p>
                            </div>
                            <div className="p-6 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Pending Review</h3>
                                <p className="text-3xl font-bold text-yellow-600 mb-1">{stats.pendingApplications}</p>
                                <p className="text-sm text-gray-600">Awaiting Decision</p>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Settings & Controls</h2>
                        <div className="space-y-4">
                            <div className="p-4 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Application Settings</h3>
                                <p className="text-gray-600 text-sm">Configure application review workflows and criteria</p>
                            </div>
                            <div className="p-4 border border-gray-200 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Notification Preferences</h3>
                                <p className="text-gray-600 text-sm">Manage email and in-app notification settings</p>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'audit' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Audit & Logs</h2>
                        <p className="text-gray-600 text-center py-8">
                            Audit logs and activity history will be displayed here.
                        </p>
                    </div>
                )}
        </DashboardLayout>
    );
};

export default StudentManagerDashboard;
